import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { VariantPicker, type Variant } from '../../../components/pickers';
import type { TaxRate } from '../api';
import type { PickedVariant } from '../lib/forms';
import { pickVariant, type UnitOption } from './units';

/** VariantPicker bound to a form's PickedVariant value. */
export function ItemPicker({
  value,
  onChange,
  error,
  autoFocus,
}: {
  value: PickedVariant | null;
  onChange: (v: PickedVariant | null) => void;
  error?: string;
  autoFocus?: boolean;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <VariantPicker
        value={value as unknown as Variant | null}
        onChange={(v) => onChange(pickVariant(v))}
        label="Item"
        autoFocus={autoFocus}
      />
      {error && (
        <Typography
          role="alert"
          variant="caption"
          color="error"
          sx={{ display: 'block', mt: 0.5, ml: 1.75 }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
}

export function UnitSelect({
  units,
  value,
  fallbackSymbol,
  onChange,
  disabled,
}: {
  units: UnitOption[];
  value: string;
  fallbackSymbol?: string;
  onChange: (u: UnitOption) => void;
  disabled?: boolean;
}) {
  const known = units.some((u) => u.unitId === value);
  return (
    <TextField
      select
      label="Unit"
      value={known ? value : value ? '__current' : ''}
      disabled={disabled || units.length === 0}
      slotProps={{
        select: {
          renderValue: (v) => units.find((u) => u.unitId === v)?.symbol ?? fallbackSymbol ?? '—',
        },
      }}
      onChange={(e) => {
        const u = units.find((x) => x.unitId === e.target.value);
        if (u) onChange(u);
      }}
    >
      {!known && value && <MenuItem value="__current">{fallbackSymbol || '—'}</MenuItem>}
      {units.map((u) => (
        <MenuItem key={u.unitId} value={u.unitId}>
          {u.symbol}
          {u.factor !== '1' ? ` (${u.factor})` : ''}
        </MenuItem>
      ))}
    </TextField>
  );
}

export function TaxSelect({
  rates,
  value,
  onChange,
  includeDefault,
  label = 'Tax',
}: {
  rates: TaxRate[];
  value: string;
  onChange: (id: string) => void;
  /** Adds "As ordered" (keep the order/receipt rate). */
  includeDefault?: boolean;
  label?: string;
}) {
  const active = rates.filter((r) => r.isActive || r.id === value);
  return (
    <TextField select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {includeDefault && <MenuItem value="default">As ordered</MenuItem>}
      <MenuItem value="">No tax</MenuItem>
      {active.map((r) => (
        <MenuItem key={r.id} value={r.id}>
          {r.name}
        </MenuItem>
      ))}
    </TextField>
  );
}
